import { categoryOptions } from './format';
import type { Budget, Category, FinanceCategoryKind, Transaction } from './types';

export type BudgetStatus = 'under' | 'in-band' | 'over';

export function budgetStatus(actual: number, min: number, max: number): BudgetStatus {
  if (actual < min) return 'under';
  if (actual > max) return 'over';
  return 'in-band';
}

/**
 * Expense USD per category. Income, transfers, credits (a negative amount), missing FX,
 * and rows with no category are skipped — the same charges the ledger calls debit.
 */
export function actualsByCategory(
  categories: Category[],
  transactions: Transaction[],
): Map<number, number> {
  const kindById = new Map(categories.map((c) => [c.id, c.kind]));
  const map = new Map<number, number>();
  for (const tx of uniqueById(transactions)) {
    if (tx.amountUsd == null || tx.categoryId == null) continue;
    if (transactionFlow(kindById, tx) !== 'expense') continue;
    map.set(tx.categoryId, (map.get(tx.categoryId) ?? 0) + Math.abs(tx.amountUsd));
  }
  return map;
}

function childrenIndex(categories: Category[]): Map<number, number[]> {
  const index = new Map<number, number[]>();
  const seen = new Set<number>();
  for (const category of categories) {
    if (seen.has(category.id)) continue;
    seen.add(category.id);
    if (category.parentId != null) {
      const list = index.get(category.parentId) ?? [];
      list.push(category.id);
      index.set(category.parentId, list);
    }
  }
  return index;
}

/** A category id plus all of its descendants. Each id is visited once. */
export function descendantIds(categories: Category[], rootId: number): number[] {
  const index = childrenIndex(categories);
  const out: number[] = [];
  const seen = new Set<number>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const child of index.get(id) ?? []) stack.push(child);
  }
  return out;
}

/** One row per transaction id. A repeated id is the same charge fetched twice. */
function uniqueById(transactions: Transaction[]): Transaction[] {
  const seen = new Set<number>();
  const out: Transaction[] = [];
  for (const tx of transactions) {
    if (seen.has(tx.id)) continue;
    seen.add(tx.id);
    out.push(tx);
  }
  return out;
}

export function rolledUpActual(
  categories: Category[],
  actuals: Map<number, number>,
  rootId: number,
): number {
  let sum = 0;
  for (const id of descendantIds(categories, rootId)) {
    sum += actuals.get(id) ?? 0;
  }
  return sum;
}

export type BudgetReportRow = {
  categoryId: number;
  label: string;
  kind: FinanceCategoryKind;
  minUsd: number;
  maxUsd: number;
  actualUsd: number;
  varianceUsd: number;
  variancePct: number | null;
  status: BudgetStatus;
};

export type BudgetReport = {
  rows: BudgetReportRow[];
  /** Expense charges whose category is not inside any budget row. */
  unbudgetedUsd: number;
  /** Spend with no category. The ledger counts this as an expense; budget rows cannot. */
  uncategorizedUsd: number;
  /** Categorized expense charges only. */
  totalActualUsd: number;
  /** Every expense charge, including uncategorized. Matches the ledger debit. */
  expenseUsd: number;
};

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Expense, income, or transfer.
 * A destination account, or a transfer category, makes the row a transfer.
 * A negative amount is a credit (income) even on an expense category.
 * A positive amount follows the category: an income category is income,
 * and every other row is an expense.
 */
export function transactionFlow(
  kindById: Map<number, FinanceCategoryKind>,
  tx: Pick<Transaction, 'categoryId' | 'accountId' | 'toAccountId' | 'amount'>,
): FinanceCategoryKind {
  if (tx.toAccountId != null && tx.toAccountId !== tx.accountId) return 'transfer';
  const kind = tx.categoryId == null ? undefined : kindById.get(tx.categoryId);
  if (kind === 'transfer') return 'transfer';
  if (tx.amount < 0) return 'income';
  if (kind === 'income') return 'income';
  return 'expense';
}

/**
 * Budget-vs-actual at the budgeted-category granularity. Actuals roll up a
 * budgeted category's descendants (budgets sit on high-level categories while
 * transactions tag leaves). Transfer categories are excluded.
 */
export function buildBudgetReport(
  categories: Category[],
  budgets: Budget[],
  transactions: Transaction[],
): BudgetReport {
  const source = uniqueById(transactions);
  const actuals = actualsByCategory(categories, source);
  const byId = new Map(categories.map((c) => [c.id, c]));
  const opts = categoryOptions(categories);
  const labelById = new Map(opts.map((o) => [o.id, o.label]));
  const order = new Map(opts.map((o, i) => [o.id, i]));

  const rows: BudgetReportRow[] = [];

  const budgetRows = budgets
    .map((budget) => ({ budget, category: byId.get(budget.categoryId) }))
    .filter((entry) => entry.category != null && entry.category.kind !== 'transfer');

  const covered = new Set<number>();
  for (const { budget } of budgetRows) {
    for (const id of descendantIds(categories, budget.categoryId)) covered.add(id);
  }

  for (const { budget, category } of budgetRows) {
    const min = budget.minUsd ?? 0;
    const max = budget.maxUsd ?? 0;
    const actual = round2(rolledUpActual(categories, actuals, budget.categoryId));
    const status = budgetStatus(actual, min, max);
    let varianceUsd = 0;
    let variancePct: number | null = 0;
    if (status === 'over') {
      varianceUsd = round2(actual - max);
      variancePct = max === 0 ? null : round2(varianceUsd / max);
    } else if (status === 'under') {
      varianceUsd = round2(actual - min);
      variancePct = min === 0 ? null : round2(varianceUsd / min);
    }
    rows.push({
      categoryId: budget.categoryId,
      label: labelById.get(budget.categoryId) ?? category!.name,
      kind: category!.kind,
      minUsd: min,
      maxUsd: max,
      actualUsd: actual,
      varianceUsd,
      variancePct,
      status,
    });
  }

  rows.sort((a, b) => (order.get(a.categoryId) ?? 0) - (order.get(b.categoryId) ?? 0));

  let totalActual = 0;
  let uncategorized = 0;
  let uncovered = 0;
  let expense = 0;
  const kindById = new Map(categories.map((c) => [c.id, c.kind]));
  for (const tx of source) {
    if (tx.amountUsd == null) continue;
    if (transactionFlow(kindById, tx) !== 'expense') continue;
    const magnitude = Math.abs(tx.amountUsd);
    expense += magnitude;
    if (tx.categoryId == null) uncategorized += magnitude;
    else {
      totalActual += magnitude;
      if (!covered.has(tx.categoryId)) uncovered += magnitude;
    }
  }

  return {
    rows,
    unbudgetedUsd: round2(uncovered),
    uncategorizedUsd: round2(uncategorized),
    totalActualUsd: round2(totalActual),
    expenseUsd: round2(expense),
  };
}

export type UnbudgetedSpendRow = {
  categoryId: number;
  label: string;
  actualUsd: number;
};

/**
 * Expense categories with spend this period that no budget covers — i.e. neither
 * the category nor any ancestor is budgeted. Each row recommends the highest
 * ancestor whose entire subtree is budget-free, so adding one budget there
 * covers all the uncovered spend beneath it (and sibling subtrees that already
 * have budgets are left alone). Income and transfers are excluded.
 */
export function unbudgetedSpend(
  categories: Category[],
  budgets: Budget[],
  transactions: Transaction[],
): UnbudgetedSpendRow[] {
  const actuals = actualsByCategory(categories, transactions);
  const byId = new Map(categories.map((c) => [c.id, c]));
  const budgetedIds = new Set(budgets.map((b) => b.categoryId));

  const childrenByParent = new Map<number, number[]>();
  for (const c of categories) {
    if (c.parentId != null) {
      const list = childrenByParent.get(c.parentId) ?? [];
      list.push(c.id);
      childrenByParent.set(c.parentId, list);
    }
  }

  const subtreeBudgetCache = new Map<number, boolean>();
  const subtreeHasBudget = (id: number): boolean => {
    const cached = subtreeBudgetCache.get(id);
    if (cached !== undefined) return cached;
    let result = budgetedIds.has(id);
    if (!result) {
      for (const child of childrenByParent.get(id) ?? []) {
        if (subtreeHasBudget(child)) {
          result = true;
          break;
        }
      }
    }
    subtreeBudgetCache.set(id, result);
    return result;
  };

  const isCovered = (id: number): boolean => {
    let cur: number | null = id;
    while (cur != null) {
      if (budgetedIds.has(cur)) return true;
      cur = byId.get(cur)?.parentId ?? null;
    }
    return false;
  };

  // Climb to the highest ancestor whose own subtree is still budget-free.
  const recommendedTarget = (id: number): number => {
    let target = id;
    for (;;) {
      const parentId = byId.get(target)?.parentId ?? null;
      if (parentId == null || subtreeHasBudget(parentId)) break;
      target = parentId;
    }
    return target;
  };

  const targets = new Set<number>();
  for (const c of categories) {
    if (c.kind !== 'expense') continue;
    if ((actuals.get(c.id) ?? 0) <= 0) continue;
    if (isCovered(c.id)) continue;
    targets.add(recommendedTarget(c.id));
  }

  const opts = categoryOptions(categories);
  const labelById = new Map(opts.map((o) => [o.id, o.label]));
  const order = new Map(opts.map((o, i) => [o.id, i]));

  return [...targets]
    .map((id) => ({
      categoryId: id,
      label: labelById.get(id) ?? byId.get(id)?.name ?? String(id),
      actualUsd: round2(rolledUpActual(categories, actuals, id)),
    }))
    .filter((row) => row.actualUsd > 0)
    .sort((a, b) => (order.get(a.categoryId) ?? 0) - (order.get(b.categoryId) ?? 0));
}

export type BudgetReportTotals = {
  minUsd: number;
  maxUsd: number;
  actualUsd: number;
  varianceUsd: number;
};

/** Column totals. Actual is every expense charge (the ledger debit), not the sum of rows. */
export function budgetReportTotals(report: BudgetReport): BudgetReportTotals {
  let minUsd = 0;
  let maxUsd = 0;
  let varianceUsd = 0;
  for (const row of report.rows) {
    minUsd += row.minUsd;
    maxUsd += row.maxUsd;
    varianceUsd += row.varianceUsd;
  }
  return {
    minUsd: round2(minUsd),
    maxUsd: round2(maxUsd),
    actualUsd: report.expenseUsd,
    varianceUsd: round2(varianceUsd),
  };
}

export type IncomeExpense = {
  incomeUsd: number;
  expenseUsd: number;
  netUsd: number;
  transferUsd: number;
};

export function incomeExpenseTotals(
  categories: Category[],
  transactions: Transaction[],
): IncomeExpense {
  const kindById = new Map(categories.map((c) => [c.id, c.kind]));
  let income = 0;
  let expense = 0;
  let transfer = 0;
  for (const tx of uniqueById(transactions)) {
    if (tx.amountUsd == null) continue;
    const flow = transactionFlow(kindById, tx);
    const magnitude = Math.abs(tx.amountUsd);
    if (flow === 'transfer') transfer += magnitude;
    else if (flow === 'income') income += magnitude;
    else expense += magnitude;
  }
  income = round2(income);
  expense = round2(expense);
  transfer = round2(transfer);
  return {
    incomeUsd: income,
    expenseUsd: expense,
    netUsd: round2(income - expense),
    transferUsd: transfer,
  };
}

export type ScaledFlowTotals = {
  income: number;
  expense: number;
  net: number;
  transfers: number;
};

/** Express USD flow totals in another currency. `rateToUsd` is units of that currency per 1 USD. */
export function scaleFlowTotals(
  totals: Pick<IncomeExpense, 'incomeUsd' | 'expenseUsd' | 'transferUsd'>,
  rateToUsd: number,
): ScaledFlowTotals {
  const income = round2(totals.incomeUsd * rateToUsd);
  const expense = round2(totals.expenseUsd * rateToUsd);
  const transfers = round2(totals.transferUsd * rateToUsd);
  return { income, expense, transfers, net: round2(income - expense) };
}

export type TrendMetric = 'budget' | 'actual' | 'difference';

/** Which number from a category's min/max band to plot on the budget trend. */
export type BudgetBandStat = 'min' | 'max' | 'average';

export function budgetBandAmount(budget: Budget, stat: BudgetBandStat): number {
  const min = budget.minUsd ?? 0;
  const max = budget.maxUsd ?? 0;
  if (stat === 'min') return min;
  if (stat === 'max') return max;
  return round2((min + max) / 2);
}

export type TrendRow = {
  categoryId: number;
  label: string;
  /** One value per month, aligned to `TrendMatrix.months`. */
  values: number[];
  total: number;
};

export type TrendMatrix = {
  months: string[];
  rows: TrendRow[];
  totalsByMonth: number[];
  grandTotal: number;
};

/** Synthetic row ids for expense charges that have no top-level expense category. */
export const UNCATEGORIZED_CATEGORY_ID = -1;
export const OTHER_EXPENSE_CATEGORY_ID = -2;

/**
 * A category × month matrix for the over-time reports. Rows are top-level
 * categories, with budgets and actuals rolled up over descendants:
 *  - `budget`     → summed budget band (`budgetStat`: min, max, or midpoint)
 *  - `actual`     → expense charges (the ledger debit). Income categories are omitted.
 *    Uncategorized charges and charges outside an expense category get their own rows.
 *    The month total is the debit, not the sum of rounded rows.
 *  - `difference` → that actual − budget max (positive = over budget)
 * Transfers and credits are excluded. All-zero rows are dropped.
 */
export function buildTrendMatrix(
  categories: Category[],
  budgetsByMonth: Map<string, Budget[]>,
  transactions: Transaction[],
  months: string[],
  metric: TrendMetric,
  budgetStat: BudgetBandStat = 'max',
): TrendMatrix {
  const txByMonth = new Map<string, Transaction[]>();
  for (const tx of uniqueById(transactions)) {
    const m = tx.date.slice(0, 7);
    const list = txByMonth.get(m) ?? [];
    list.push(tx);
    txByMonth.set(m, list);
  }

  const actualsPerMonth = new Map<string, Map<number, number>>();
  const budgetPerMonth = new Map<string, Map<number, number>>();
  for (const m of months) {
    actualsPerMonth.set(m, actualsByCategory(categories, txByMonth.get(m) ?? []));
    const budgetMap = new Map<number, number>();
    const stat = metric === 'budget' ? budgetStat : 'max';
    for (const b of budgetsByMonth.get(m) ?? []) {
      budgetMap.set(b.categoryId, budgetBandAmount(b, stat));
    }
    budgetPerMonth.set(m, budgetMap);
  }

  const spending = metric !== 'budget';
  const topLevel = categories
    .filter((c) => {
      if (c.parentId != null || c.kind === 'transfer') return false;
      if (spending && c.kind === 'income') return false;
      return true;
    })
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  const reported = new Set<number>();
  for (const cat of topLevel) {
    if (cat.kind !== 'expense') continue;
    for (const id of descendantIds(categories, cat.id)) reported.add(id);
  }

  const totalsByMonth = months.map(() => 0);
  const rows: TrendRow[] = [];

  const pushRow = (categoryId: number, label: string, values: number[]) => {
    if (values.some((v) => v !== 0)) {
      rows.push({ categoryId, label, values, total: round2(sum(values)) });
    }
  };

  for (const cat of topLevel) {
    const values = months.map((m, i) => {
      const actual = rolledUpActual(categories, actualsPerMonth.get(m)!, cat.id);
      // rolledUpActual sums a {id → value} map over the subtree — reused for budgets.
      const budget = rolledUpActual(categories, budgetPerMonth.get(m)!, cat.id);
      const value = metric === 'budget' ? budget : metric === 'actual' ? actual : actual - budget;
      const rounded = round2(value);
      if (metric !== 'actual') totalsByMonth[i]! += rounded;
      return rounded;
    });
    pushRow(cat.id, cat.name, values);
  }

  if (spending) {
    const kindById = new Map(categories.map((c) => [c.id, c.kind]));
    const leftovers = months.map((m) => {
      let uncategorized = 0;
      let other = 0;
      for (const tx of txByMonth.get(m) ?? []) {
        if (tx.amountUsd == null) continue;
        if (transactionFlow(kindById, tx) !== 'expense') continue;
        const magnitude = Math.abs(tx.amountUsd);
        if (tx.categoryId == null) uncategorized += magnitude;
        else if (!reported.has(tx.categoryId)) other += magnitude;
      }
      return { uncategorized, other };
    });
    for (const [id, label, pick] of [
      [UNCATEGORIZED_CATEGORY_ID, 'Uncategorized', 'uncategorized'],
      [OTHER_EXPENSE_CATEGORY_ID, 'Other expenses', 'other'],
    ] as const) {
      const values = leftovers.map((row, i) => {
        const rounded = round2(row[pick]);
        if (metric === 'difference') totalsByMonth[i]! += rounded;
        return rounded;
      });
      pushRow(id, label, values);
    }
  }

  if (metric === 'actual') {
    for (let i = 0; i < months.length; i += 1) {
      totalsByMonth[i] = incomeExpenseTotals(
        categories,
        txByMonth.get(months[i]!) ?? [],
      ).expenseUsd;
    }
  }

  const totals = totalsByMonth.map(round2);
  return { months, rows, totalsByMonth: totals, grandTotal: round2(sum(totals)) };
}

function sum(values: number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

export type MonthlyIncomeExpense = { month: string; income: number; expense: number };

/** Income/expense bucketed by `YYYY-MM`, sorted ascending. Transfers excluded. */
export function monthlyIncomeExpense(
  categories: Category[],
  transactions: Transaction[],
): MonthlyIncomeExpense[] {
  const kindById = new Map(categories.map((c) => [c.id, c.kind]));
  const buckets = new Map<string, { income: number; expense: number }>();
  for (const tx of uniqueById(transactions)) {
    if (tx.amountUsd == null) continue;
    const flow = transactionFlow(kindById, tx);
    if (flow === 'transfer') continue;
    const month = tx.date.slice(0, 7);
    const bucket = buckets.get(month) ?? { income: 0, expense: 0 };
    const magnitude = Math.abs(tx.amountUsd);
    if (flow === 'income') bucket.income += magnitude;
    else bucket.expense += magnitude;
    buckets.set(month, bucket);
  }
  return [...buckets.entries()]
    .map(([month, value]) => ({
      month,
      income: round2(value.income),
      expense: round2(value.expense),
    }))
    .sort((a, b) => a.month.localeCompare(b.month));
}

export type DrillScope =
  | { kind: 'category'; categoryId: number }
  | { kind: 'uncategorized' }
  | { kind: 'other' }
  | { kind: 'unbudgeted' }
  | { kind: 'all-expenses' };

export function drillScopeForCategory(categoryId: number): DrillScope {
  if (categoryId === UNCATEGORIZED_CATEGORY_ID) return { kind: 'uncategorized' };
  if (categoryId === OTHER_EXPENSE_CATEGORY_ID) return { kind: 'other' };
  return { kind: 'category', categoryId };
}

export type DrillLine = { label: string; usd: number };

export type DrillGroup = {
  key: string;
  title: string;
  transactions: Transaction[];
  /** Null when the rows have no USD amount (missing FX). */
  totalUsd: number | null;
};

export type BudgetDrillLine = { categoryId: number; month: string; usd: number };

export type ActualDrillModel = {
  summary: DrillLine[];
  note: string | null;
  budgetLines: BudgetDrillLine[];
  groups: DrillGroup[];
};

function kindMap(categories: Category[]): Map<number, FinanceCategoryKind> {
  return new Map(categories.map((c) => [c.id, c.kind]));
}

function byDate(a: Transaction, b: Transaction): number {
  const byDay = a.date.localeCompare(b.date);
  return byDay !== 0 ? byDay : a.id - b.id;
}

/** Absolute USD of the rows, rounded once. Rows with no FX contribute nothing. */
export function sumAmountUsd(transactions: Transaction[]): number {
  let total = 0;
  for (const tx of transactions) {
    if (tx.amountUsd != null) total += Math.abs(tx.amountUsd);
  }
  return round2(total);
}

function expenseRootIds(categories: Category[]): Set<number> {
  const ids = new Set<number>();
  for (const category of categories) {
    if (category.parentId == null && category.kind === 'expense') {
      for (const id of descendantIds(categories, category.id)) ids.add(id);
    }
  }
  return ids;
}

function coveredCategoryIds(categories: Category[], budgets: Budget[]): Set<number> {
  const ids = new Set<number>();
  const byId = new Map(categories.map((c) => [c.id, c]));
  for (const budget of budgets) {
    const category = byId.get(budget.categoryId);
    if (!category || category.kind === 'transfer') continue;
    for (const id of descendantIds(categories, budget.categoryId)) ids.add(id);
  }
  return ids;
}

function matchesScope(
  tx: Transaction,
  scope: DrillScope,
  expenseIds: Set<number>,
  descendants: Set<number>,
  covered: Set<number>,
  kinds: Map<number, FinanceCategoryKind>,
): boolean {
  if (scope.kind === 'all-expenses') return true;
  if (scope.kind === 'uncategorized') return tx.categoryId == null;
  if (scope.kind === 'category') return tx.categoryId != null && descendants.has(tx.categoryId);
  if (tx.categoryId == null) return false;
  const kind = kinds.get(tx.categoryId);
  if (kind === 'income' || kind === 'transfer') return false;
  if (scope.kind === 'unbudgeted') return !covered.has(tx.categoryId);
  return !expenseIds.has(tx.categoryId);
}

function idsOverlap(categories: Category[], ids: number[]): boolean {
  const set = new Set(ids);
  const byId = new Map(categories.map((c) => [c.id, c]));
  for (const id of set) {
    let parent = byId.get(id)?.parentId ?? null;
    while (parent != null) {
      if (set.has(parent)) return true;
      parent = byId.get(parent)?.parentId ?? null;
    }
  }
  return false;
}

function budgetLinesFor(
  categories: Category[],
  budgets: Budget[],
  scope: DrillScope,
  month: string | null,
  windowMonths: string[],
): { lines: BudgetDrillLine[]; totalUsd: number; overlaps: boolean } {
  if (scope.kind !== 'category' && scope.kind !== 'all-expenses') {
    return { lines: [], totalUsd: 0, overlaps: false };
  }
  const allowed =
    scope.kind === 'category'
      ? new Set(descendantIds(categories, scope.categoryId))
      : expenseRootIds(categories);
  const lines: BudgetDrillLine[] = [];
  const idsByMonth = new Map<string, number[]>();
  for (const budget of budgets) {
    const key = budget.month.slice(0, 7);
    if (month != null ? key !== month : !windowMonths.includes(key)) continue;
    if (!allowed.has(budget.categoryId)) continue;
    const usd = budget.maxUsd ?? 0;
    if (usd === 0) continue;
    lines.push({ categoryId: budget.categoryId, month: key, usd });
    const ids = idsByMonth.get(key) ?? [];
    ids.push(budget.categoryId);
    idsByMonth.set(key, ids);
  }
  lines.sort((a, b) => a.month.localeCompare(b.month) || a.categoryId - b.categoryId);
  let overlaps = false;
  for (const ids of idsByMonth.values()) {
    if (idsOverlap(categories, ids)) overlaps = true;
  }
  return { lines, totalUsd: round2(sum(lines.map((line) => line.usd))), overlaps };
}

/**
 * Transactions behind one actual or actual-minus-budget cell.
 * `windowMonths` limits an all-months cell to the months on the report.
 */
export function buildActualDrillModel(input: {
  categories: Category[];
  transactions: Transaction[];
  budgets: Budget[];
  scope: DrillScope;
  month: string | null;
  windowMonths: string[];
  mode: 'actual' | 'difference';
  cellUsd: number;
}): ActualDrillModel {
  const { categories, scope, month, windowMonths, mode, cellUsd } = input;
  const transactions = uniqueById(input.transactions);
  const kinds = kindMap(categories);
  const expenseIds = expenseRootIds(categories);
  const descendants =
    scope.kind === 'category'
      ? new Set(descendantIds(categories, scope.categoryId))
      : new Set<number>();
  const covered = coveredCategoryIds(categories, input.budgets);

  const included: Transaction[] = [];
  const credits: Transaction[] = [];
  const transfers: Transaction[] = [];
  const missingFx: Transaction[] = [];

  for (const tx of transactions) {
    const key = tx.date.slice(0, 7);
    if (month != null ? key !== month : !windowMonths.includes(key)) continue;
    if (!matchesScope(tx, scope, expenseIds, descendants, covered, kinds)) continue;
    if (tx.amountUsd == null) {
      missingFx.push(tx);
      continue;
    }
    const flow = transactionFlow(kinds, tx);
    if (flow === 'expense') included.push(tx);
    else if (flow === 'income') credits.push(tx);
    else transfers.push(tx);
  }

  included.sort(byDate);
  credits.sort(byDate);
  transfers.sort(byDate);
  missingFx.sort(byDate);

  const includedUsd = sumAmountUsd(included);
  const summary: DrillLine[] = [];
  const notes: string[] = [];
  let budgetLines: BudgetDrillLine[] = [];

  if (mode === 'difference') {
    const budget = budgetLinesFor(categories, input.budgets, scope, month, windowMonths);
    budgetLines = budget.lines;
    summary.push({ label: 'Expense charges', usd: includedUsd });
    summary.push({ label: 'Budget max', usd: budget.totalUsd });
    summary.push({ label: 'This cell', usd: cellUsd });
    if (Math.abs(round2(includedUsd - budget.totalUsd) - cellUsd) >= 0.01) {
      notes.push('This cell rounds each category before the months are added.');
    }
    if (budget.overlaps) {
      notes.push('A budget on both a category and a subcategory is added together.');
    }
  } else {
    summary.push({ label: 'Expense charges', usd: includedUsd });
    if (Math.abs(includedUsd - cellUsd) >= 0.01) {
      summary.push({ label: 'Report cell', usd: cellUsd });
      notes.push('The report cell rounds each category before adding, so it can differ by a cent.');
    }
  }

  if (
    scope.kind === 'category' &&
    included.some((tx) => tx.categoryId != null && tx.categoryId !== scope.categoryId)
  ) {
    notes.push('Subcategory charges are included in this category.');
  }
  if (credits.length > 0) {
    summary.push({ label: 'Credits left out', usd: sumAmountUsd(credits) });
    notes.push('Credits stay on the ledger credit total and are not subtracted here.');
  }
  if (transfers.length > 0) {
    summary.push({ label: 'Transfers left out', usd: sumAmountUsd(transfers) });
  }

  const groups: DrillGroup[] = [
    { key: 'included', title: 'Expense charges', transactions: included, totalUsd: includedUsd },
  ];
  if (credits.length > 0) {
    groups.push({
      key: 'credits',
      title: 'Credits',
      transactions: credits,
      totalUsd: sumAmountUsd(credits),
    });
  }
  if (transfers.length > 0) {
    groups.push({
      key: 'transfers',
      title: 'Transfers',
      transactions: transfers,
      totalUsd: sumAmountUsd(transfers),
    });
  }
  if (missingFx.length > 0) {
    groups.push({
      key: 'missing-fx',
      title: 'Missing an FX rate',
      transactions: missingFx,
      totalUsd: null,
    });
  }

  return { summary, note: notes.length > 0 ? notes.join(' ') : null, budgetLines, groups };
}
